/**
 * AdvisoryBoardDiagram — the animated "advisory board" node network: your idea
 * at the hub, the three specialist advisor agents (Product Strategist, IP
 * Consultant, Financial Analyst) as labelled nodes around it with satellite
 * sub-analysis dots, brass signal pulses flowing every connection, an orbiting
 * data ring, and hover/tap tooltips describing each advisor.
 *
 * Self-contained: it carries its own eyebrow + caption + SVG + hover state + a
 * namespaced (`abd-*`) <style> block, so it renders identically on any light
 * ground. Used in the /ve hero and grouped under the main landing's
 * "How it works" section — one source, no drift.
 */
import { useState } from 'react';

/** Which of the three advisor nodes is currently revealing its tooltip. */
type AdvisorKey = 'product' | 'ip' | 'financial';

interface AdvisorNode {
  key: AdvisorKey;
  color: string;
  cx: number;
  cy: number;
  nodeDelay: string;
  labelX: number;
  labelY: number;
  labelDelay: string;
  label: string;
  name: string;
  desc: string;
  hit: { x: number; y: number; w: number; h: number };
  tip: { x: number; y: number; w: number; h: number };
}

const NM_EDGES: { x1: number; y1: number; x2: number; y2: number; t: number; d: number; f: number }[] = [
  // hub → three advisor nodes
  { x1: 270, y1: 168, x2: 270, y2: 78, t: 0.3, d: 0.0, f: 0.0 },
  { x1: 270, y1: 168, x2: 92, y2: 220, t: 0.3, d: 0.1, f: 0.7 },
  { x1: 270, y1: 168, x2: 448, y2: 220, t: 0.3, d: 0.2, f: 1.4 },
  // product strategist → its three sub-topics
  { x1: 270, y1: 78, x2: 214, y2: 52, t: 0.22, d: 0.5, f: 0.3 },
  { x1: 270, y1: 78, x2: 270, y2: 44, t: 0.22, d: 0.55, f: 0.9 },
  { x1: 270, y1: 78, x2: 326, y2: 52, t: 0.22, d: 0.6, f: 1.6 },
  // ip consultant → its three sub-topics
  { x1: 92, y1: 220, x2: 48, y2: 196, t: 0.22, d: 0.65, f: 0.5 },
  { x1: 92, y1: 220, x2: 40, y2: 246, t: 0.22, d: 0.7, f: 1.1 },
  { x1: 92, y1: 220, x2: 120, y2: 258, t: 0.22, d: 0.75, f: 1.9 },
  // financial analyst → its three sub-topics
  { x1: 448, y1: 220, x2: 492, y2: 196, t: 0.22, d: 0.8, f: 0.4 },
  { x1: 448, y1: 220, x2: 500, y2: 246, t: 0.22, d: 0.85, f: 1.3 },
  { x1: 448, y1: 220, x2: 420, y2: 258, t: 0.22, d: 0.9, f: 2.0 },
  // cross-links between advisors (the web reads as interconnected)
  { x1: 92, y1: 220, x2: 448, y2: 220, t: 0.14, d: 1.0, f: 2.4 },
  { x1: 270, y1: 78, x2: 92, y2: 220, t: 0.14, d: 1.05, f: 1.7 },
  { x1: 270, y1: 78, x2: 448, y2: 220, t: 0.14, d: 1.1, f: 2.1 },
  // grandchild relays extending each branch outward
  { x1: 214, y1: 52, x2: 182, y2: 34, t: 0.16, d: 1.2, f: 2.6 },
  { x1: 326, y1: 52, x2: 358, y2: 34, t: 0.16, d: 1.25, f: 2.8 },
  { x1: 48, y1: 196, x2: 22, y2: 176, t: 0.16, d: 1.3, f: 3.0 },
  { x1: 120, y1: 258, x2: 110, y2: 284, t: 0.16, d: 1.35, f: 3.2 },
  { x1: 492, y1: 196, x2: 518, y2: 176, t: 0.16, d: 1.4, f: 3.4 },
  { x1: 420, y1: 258, x2: 430, y2: 284, t: 0.16, d: 1.45, f: 3.6 },
];

/** The three specialist agents; positions/labels match the hand-placed SVG. */
const NM_ADVISORS: AdvisorNode[] = [
  {
    key: 'product',
    color: '#1F9D63',
    cx: 270,
    cy: 78,
    nodeDelay: '0s',
    labelX: 270,
    labelY: 16,
    labelDelay: '.8s',
    label: 'PRODUCT STRATEGIST',
    name: 'Product Strategist',
    desc: 'Pressure-tests whether a real market need exists: demand, competitors, and the target buyer.',
    hit: { x: 180, y: 4, w: 180, h: 94 },
    tip: { x: 167, y: 2, w: 206, h: 62 },
  },
  {
    key: 'ip',
    color: '#9c4a53',
    cx: 92,
    cy: 220,
    nodeDelay: '.15s',
    labelX: 72,
    labelY: 290,
    labelDelay: '.95s',
    label: 'IP CONSULTANT',
    name: 'IP Consultant',
    desc: 'Assesses patentability: searches prior art, shapes claims, and maps the filing path.',
    hit: { x: 6, y: 206, w: 150, h: 92 },
    tip: { x: -14, y: 232, w: 206, h: 62 },
  },
  {
    key: 'financial',
    color: '#181712',
    cx: 448,
    cy: 220,
    nodeDelay: '.3s',
    labelX: 474,
    labelY: 290,
    labelDelay: '1.1s',
    label: 'FINANCIAL ANALYST',
    name: 'Financial Analyst',
    desc: 'Builds the business case: pricing, unit economics, projections, and a valuation range.',
    hit: { x: 396, y: 206, w: 158, h: 92 },
    tip: { x: 348, y: 232, w: 206, h: 62 },
  },
];

export function AdvisoryBoardDiagram({ className = '' }: { className?: string }) {
  // Hover (or tap, on touch) an advisor node/label to reveal what it does.
  const [activeAdvisor, setActiveAdvisor] = useState<AdvisorKey | null>(null);
  const activeAdv = activeAdvisor ? NM_ADVISORS.find((a) => a.key === activeAdvisor) ?? null : null;

  return (
    <div className={`abd${className ? ` ${className}` : ''}`}>
      <style>{ABD_CSS}</style>
      <span className="abd-eyebrow">Advisory board</span>
      <p className="abd-cap">
        Advisory boards used to cost a fortune.
        <br />
        <em>Now yours runs 24/7 on 10+ AI agents.</em>
      </p>
      <div className="abd-stage">
        <svg className="abd-nodemap" viewBox="-16 0 572 300" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          {/* convergence halo behind centre */}
          <circle className="abd-nm-halo" cx="270" cy="168" r="42" fill="rgba(181,133,60,0.1)" />

          {/* signal-ping rings — periodic broadcast pulses from the hub */}
          <g stroke="#B5853C" fill="none" strokeWidth="1">
            <circle className="abd-nm-ping" cx="270" cy="168" r="20" style={{ animationDelay: '0.5s' }} />
            <circle className="abd-nm-ping" cx="270" cy="168" r="20" style={{ animationDelay: '2.5s' }} />
          </g>

          {/* base connection lines — the full web */}
          <g fill="none">
            {NM_EDGES.map((e, i) => (
              <line
                key={`l${i}`}
                className="abd-nm-line"
                pathLength={1}
                stroke={`rgba(24,23,18,${e.t})`}
                strokeWidth={e.t >= 0.28 ? 1.25 : 1}
                style={{ animationDelay: `${e.d}s` }}
                x1={e.x1}
                y1={e.y1}
                x2={e.x2}
                y2={e.y2}
              />
            ))}
          </g>
          {/* brass pulse travelling every connection on a loop, staggered */}
          <g stroke="#B5853C" strokeWidth="1.4" fill="none" strokeLinecap="round">
            {NM_EDGES.map((e, i) => (
              <line
                key={`f${i}`}
                className="abd-nm-flow"
                pathLength={1}
                style={{ animationDelay: `${e.f}s` }}
                x1={e.x1}
                y1={e.y1}
                x2={e.x2}
                y2={e.y2}
              />
            ))}
          </g>

          {/* sub-analysis nodes — small, coloured per advisor; twinkle + drift */}
          <g>
            <circle className="abd-nm-mid" style={{ animationDelay: '1.5s' }} cx="214" cy="52" r="3.2" fill="#1F9D63" />
            <circle className="abd-nm-mid" style={{ animationDelay: '1.55s' }} cx="270" cy="44" r="3.2" fill="#1F9D63" />
            <circle className="abd-nm-mid" style={{ animationDelay: '1.6s' }} cx="326" cy="52" r="3.2" fill="#1F9D63" />
            <circle className="abd-nm-mid" style={{ animationDelay: '1.65s' }} cx="48" cy="196" r="3.2" fill="#9c4a53" />
            <circle className="abd-nm-mid" style={{ animationDelay: '1.7s' }} cx="40" cy="246" r="3.2" fill="#9c4a53" />
            <circle className="abd-nm-mid" style={{ animationDelay: '1.75s' }} cx="120" cy="258" r="3.2" fill="#9c4a53" />
            <circle className="abd-nm-mid" style={{ animationDelay: '1.8s' }} cx="492" cy="196" r="3.2" fill="#181712" />
            <circle className="abd-nm-mid" style={{ animationDelay: '1.85s' }} cx="500" cy="246" r="3.2" fill="#181712" />
            <circle className="abd-nm-mid" style={{ animationDelay: '1.9s' }} cx="420" cy="258" r="3.2" fill="#181712" />
          </g>

          {/* grandchild relay dots — tiny points extending each branch outward */}
          <g>
            <circle className="abd-nm-relay" style={{ animationDelay: '2.5s' }} cx="358" cy="34" r="1.8" fill="#1F9D63" />
            <circle className="abd-nm-relay" style={{ animationDelay: '2.7s' }} cx="182" cy="34" r="1.8" fill="#1F9D63" />
            <circle className="abd-nm-relay" style={{ animationDelay: '2.9s' }} cx="22" cy="176" r="1.8" fill="#9c4a53" />
            <circle className="abd-nm-relay" style={{ animationDelay: '3.1s' }} cx="110" cy="284" r="1.8" fill="#9c4a53" />
            <circle className="abd-nm-relay" style={{ animationDelay: '3.3s' }} cx="518" cy="176" r="1.8" fill="#181712" />
            <circle className="abd-nm-relay" style={{ animationDelay: '3.5s' }} cx="430" cy="284" r="1.8" fill="#181712" />
          </g>

          {/* orbiting data dots circling the hub — a slow live ring */}
          <g className="abd-nm-orbit">
            <circle cx="302" cy="168" r="1.7" fill="#B5853C" opacity="0.5" />
            <circle cx="286" cy="196" r="1.7" fill="#B5853C" opacity="0.5" />
            <circle cx="254" cy="196" r="1.7" fill="#B5853C" opacity="0.5" />
            <circle cx="238" cy="168" r="1.7" fill="#B5853C" opacity="0.5" />
            <circle cx="254" cy="140" r="1.7" fill="#B5853C" opacity="0.5" />
            <circle cx="286" cy="140" r="1.7" fill="#B5853C" opacity="0.5" />
          </g>

          {/* three advisor nodes + role labels — each an interactive hover target */}
          {NM_ADVISORS.map((a) => (
            <g
              key={a.key}
              className="abd-nm-adv"
              onMouseEnter={() => setActiveAdvisor(a.key)}
              onMouseLeave={() => setActiveAdvisor((cur) => (cur === a.key ? null : cur))}
              onClick={() => setActiveAdvisor((cur) => (cur === a.key ? null : a.key))}
            >
              <rect x={a.hit.x} y={a.hit.y} width={a.hit.w} height={a.hit.h} fill="transparent" />
              <circle className="abd-nm-node" style={{ animationDelay: a.nodeDelay }} cx={a.cx} cy={a.cy} r="7" fill={a.color} />
              <text className="abd-nm-label" textAnchor="middle" style={{ animationDelay: a.labelDelay, fill: a.color }} x={a.labelX} y={a.labelY}>{a.label}</text>
            </g>
          ))}

          {/* the idea at the hub — non-interactive */}
          <text className="abd-nm-label abd-nm-label--idea" textAnchor="middle" style={{ animationDelay: '1.2s' }} x="270" y="200">YOUR IDEA</text>

          {/* sub-node labels — each advisor's real sub-analyses */}
          <g>
            <text className="abd-nm-sub" style={{ animationDelay: '1.55s' }} textAnchor="middle" x="206" y="40">MARKET NEED</text>
            <text className="abd-nm-sub" style={{ animationDelay: '1.6s' }} textAnchor="middle" x="270" y="32">DEMAND</text>
            <text className="abd-nm-sub" style={{ animationDelay: '1.65s' }} textAnchor="middle" x="334" y="40">COMPETITORS</text>
            <text className="abd-nm-sub" style={{ animationDelay: '1.7s' }} textAnchor="start" x="16" y="190">PRIOR ART</text>
            <text className="abd-nm-sub" style={{ animationDelay: '1.75s' }} textAnchor="start" x="16" y="244">FILING</text>
            <text className="abd-nm-sub" style={{ animationDelay: '1.8s' }} textAnchor="middle" x="120" y="274">CLAIMS</text>
            <text className="abd-nm-sub" style={{ animationDelay: '1.85s' }} textAnchor="end" x="524" y="190">PROJECTIONS</text>
            <text className="abd-nm-sub" style={{ animationDelay: '1.9s' }} textAnchor="end" x="524" y="244">FUNDING</text>
            <text className="abd-nm-sub" style={{ animationDelay: '1.95s' }} textAnchor="middle" x="420" y="274">UNIT ECON</text>
          </g>

          {/* centre — your idea: larger ring + core, scans + breathes */}
          <circle className="abd-nm-ring" cx="270" cy="168" r="23" fill="none" stroke="#B5853C" strokeWidth="1.75" strokeDasharray="3 3" opacity="0.75" />
          <circle className="abd-nm-core" cx="270" cy="168" r="13" fill="#B5853C" />

          {/* advisor tooltip — rendered last so it sits on top; opaque card,
              pointer-events:none so moving onto it never steals the hover. */}
          {activeAdv && (
            <foreignObject className="abd-nm-tip-box" x={activeAdv.tip.x} y={activeAdv.tip.y} width={activeAdv.tip.w} height={activeAdv.tip.h}>
              <div className="abd-nm-tip">
                <span className="abd-nm-tip-name" style={{ color: activeAdv.color }}>{activeAdv.name}</span>
                <span className="abd-nm-tip-desc">{activeAdv.desc}</span>
              </div>
            </foreignObject>
          )}
        </svg>
      </div>
      {/* Figure caption at the bottom of the illustration — names the diagram
          for what it actually is: EntreDash's own multi-agent orchestration (the
          same workflow documented in "The EntreDash Method"). Small, muted, and
          understated so it reads as an attribution line, not a claim banner.
          Lives in the shared component so it appears identically on /ve and the
          main page. */}
      <p className="abd-note">
        <span className="abd-note-mark" aria-hidden="true" /> EntreDash proprietary AI agent workflow
      </p>
    </div>
  );
}

const ABD_CSS = `
.abd{display:flex;flex-direction:column;align-items:center;text-align:center;}
.abd-eyebrow{font-family:'JetBrains Mono',monospace;font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:rgba(24,23,18,0.62);}
.abd-cap{font-family:'Switzer',sans-serif;font-weight:600;font-size:clamp(17px,0.85rem + 0.6vw,21px);line-height:1.3;letter-spacing:-.01em;color:#181712;margin:12px auto 14px;max-width:34ch;}
/* Brass-as-text on cream must use the deep brass text token: bright brass
   #B5853C was 2.87:1 (fails even large-text AA); --brass-deep #855F20 is
   5.01:1. The ABD only ever renders on light grounds, so this is safe. */
.abd-cap em{font-style:italic;color:var(--brass-deep);}
/* the "clearing of light": a static radial pool centred on the hub, fading
   fully out so there is no hard edge — a lightening of the paper, not a glow. */
.abd-stage{position:relative;padding:6px 0;width:100%;text-align:center;}
.abd-stage::before{content:"";position:absolute;inset:-10% -14% -12% -14%;background:radial-gradient(60% 60% at 50% 55%,#FFFFFF 0%,rgba(250,248,242,.7) 34%,rgba(245,241,232,.3) 56%,rgba(245,241,232,0) 78%);z-index:0;pointer-events:none;opacity:.94;}
.abd-stage>svg{position:relative;z-index:1;}
/* scaled up with a small symmetric bleed into the gutters so it reads large
   while the hub stays centered. */
.abd-nodemap{display:block;width:calc(100% + clamp(40px,7vw,150px));height:auto;margin:0 calc(-1 * clamp(20px,3.5vw,75px));}
.abd-nm-line{stroke-dasharray:none;stroke-dashoffset:0;}
.abd-nm-node{opacity:1;transform-box:fill-box;transform-origin:center;animation:abd-nm-drift 4.5s ease-in-out infinite, abd-nm-pulse 3s ease-in-out infinite;}
@keyframes abd-nm-drift{0%,100%{transform:translate(0,0);}25%{transform:translate(4px,-3.5px);}50%{transform:translate(-3px,-4.5px);}75%{transform:translate(-3.5px,3px);}}
@keyframes abd-nm-pulse{0%,100%{transform:scale(1);}50%{transform:scale(1.28);}}
.abd-nm-mid{opacity:1;transform-box:fill-box;transform-origin:center;animation:abd-nm-drift2 5s ease-in-out infinite, abd-nm-twinkle 2.4s ease-in-out infinite;}
@keyframes abd-nm-drift2{0%,100%{transform:translate(0,0);}33%{transform:translate(3px,3px);}66%{transform:translate(-3px,2px);}}
.abd-nm-relay{opacity:1;animation:abd-nm-twinkle 1.8s ease-in-out infinite;}
@keyframes abd-nm-twinkle{0%,100%{opacity:1;}50%{opacity:.15;}}
.abd-nm-orbit{transform-box:view-box;transform-origin:270px 168px;animation:abd-nm-spin 9s linear infinite;}
.abd-nm-ping{transform-box:fill-box;transform-origin:center;opacity:0;stroke-width:1.5;animation:abd-nm-ping 2.6s ease-out infinite;}
@keyframes abd-nm-ping{0%{transform:scale(.3);opacity:.75;}70%{opacity:0;}100%{transform:scale(2.6);opacity:0;}}
.abd-nm-halo{transform-box:fill-box;transform-origin:center;opacity:1;animation:abd-nm-halo-breathe 3s ease-in-out infinite;}
@keyframes abd-nm-halo-breathe{0%,100%{transform:scale(1);opacity:1;}50%{transform:scale(1.22);opacity:.7;}}
.abd-nm-flow{stroke-dasharray:0.2 0.8;stroke-dashoffset:1;opacity:1;stroke-width:2;animation:abd-nm-flow 2.1s linear infinite;}
@keyframes abd-nm-flow{from{stroke-dashoffset:1;}to{stroke-dashoffset:0;}}
.abd-nm-ring{transform-box:fill-box;transform-origin:center;animation:abd-nm-spin 5.5s linear infinite;}
@keyframes abd-nm-spin{to{transform:rotate(360deg);}}
.abd-nm-core{transform-box:fill-box;transform-origin:center;animation:abd-nm-breathe 2s ease-in-out infinite;}
@keyframes abd-nm-breathe{0%,100%{transform:scale(1);}50%{transform:scale(1.28);}}
/* advisor hover targets — node + role label share one pointer group. Emphasis
   on hover uses stroke + shadow (not transform) so it never fights the node's
   drift/pulse keyframes, which already animate transform. */
.abd-nm-adv{cursor:pointer;}
.abd-nm-adv .abd-nm-node{transition:stroke-width .16s ease,filter .16s ease;}
.abd-nm-adv:hover .abd-nm-node{stroke:#FFFFFF;stroke-width:3;filter:drop-shadow(0 1px 3px rgba(24,23,18,.35));}
.abd-nm-tip-box{overflow:visible;pointer-events:none;}
.abd-nm-tip{pointer-events:none;box-sizing:border-box;display:flex;flex-direction:column;gap:3px;background:#FFFFFF;border:1px solid rgba(24,23,18,.1);border-radius:10px;padding:8px 11px;box-shadow:0 12px 28px rgba(24,23,18,.16);font-family:'Switzer',sans-serif;animation:abd-nm-tip-in .16s ease-out;}
.abd-nm-tip-name{font-weight:700;font-size:12.5px;line-height:1.15;letter-spacing:.005em;}
.abd-nm-tip-desc{font-size:11px;line-height:1.32;color:rgba(24,23,18,.66);}
@keyframes abd-nm-tip-in{from{opacity:0;transform:translateY(3px);}to{opacity:1;transform:translateY(0);}}
.abd-nm-label{font-family:'JetBrains Mono',monospace;font-weight:700;font-size:13.5px;letter-spacing:.05em;fill:rgba(24,23,18,.72);opacity:1;}
.abd-nm-label--idea{fill:#B5853C;font-weight:700;font-size:16px;letter-spacing:.09em;}
.abd-nm-sub{font-family:'JetBrains Mono',monospace;font-size:11px;letter-spacing:.03em;fill:rgba(24,23,18,.64);opacity:1;}
/* bottom attribution caption — understated mono credit line with a small brass
   diamond mark, centred under the diagram. */
.abd-note{display:flex;align-items:center;justify-content:center;gap:10px;margin:18px auto 0;font-family:'JetBrains Mono',monospace;font-size:13px;letter-spacing:.13em;text-transform:uppercase;color:var(--text-decorative);}
.abd-note-mark{width:6px;height:6px;flex:none;background:#B5853C;transform:rotate(45deg);border-radius:1px;}
@media (prefers-reduced-motion:reduce){
  .abd-nm-line{stroke-dashoffset:0;animation:none;}
  .abd-nm-node,.abd-nm-mid{opacity:1;animation:none;}
  .abd-nm-mid{opacity:.7;}
  .abd-nm-relay{opacity:.6;animation:none;}
  .abd-nm-halo{opacity:1;animation:none;}
  .abd-nm-label,.abd-nm-sub{opacity:1;animation:none;}
  .abd-nm-flow{display:none;}
  .abd-nm-ping{display:none;}
  .abd-nm-orbit{animation:none;}
  .abd-nm-ring,.abd-nm-core{animation:none;}
  .abd-nm-tip{animation:none;}
  .abd-nm-adv .abd-nm-node{transition:none;}
}
`;
